// Implements: F-IDN-012, F-UIX-009
/**
 * "Cambiar la contraseña" (v3 `acctPassword`): current, new and repeated new password, with the
 * shared strength meter (`PasswordStrength`, as on the reset page) under the new one. Pure
 * presentation with the three drafts in local state.
 *
 * What it refuses itself, with no request: a missing field, a new password shorter than the
 * server's minimum and a confirmation that does not match (focused, `aria-invalid`). What it is
 * told: `wrongCurrent` (the server's `401`, said at the top with `role="alert"`), `doneAt` (the
 * time of the last change) and `waitSeconds` (a `429`). `onSubmit` answers how the change ended:
 * `changed` empties the fields; `formRefused` is a sentence for the whole form; `newRefused` carries
 * the server's sentence about the new password
 * (its `422` policy refusal), said under that field and tied to it by `aria-describedby`, until the
 * person types a different one. A `newRefused` about a value the person has since edited is stale
 * and ignored (checklist 14). The server stays the authority; the length check only saves a trip.
 */

import { useId, useRef, useState } from "react";
import type { ReactElement, RefObject } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { PasswordStrength } from "../../../shared/ui/molecules/PasswordStrength";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { isBelowMinimum } from "../../../shared/ui/molecules/passwordScore";
import shared from "./account.module.css";

export interface PasswordFormProps {
  readonly saving: boolean;
  readonly wrongCurrent: boolean;
  /** "18:40" after a change in this visit, else `null`. */
  readonly doneAt: string | null;
  /** Seconds left of a `429`, else `null`. */
  readonly waitSeconds: number | null;
  readonly onSubmit: (current: string, next: string) => Promise<PasswordSubmitOutcome>;
}

/** How a submitted change ended, as the container read the server's answer. */
export type PasswordSubmitOutcome =
  | { readonly kind: "changed" }
  | { readonly kind: "refused" }
  | { readonly kind: "formRefused"; readonly message: string }
  | { readonly kind: "newRefused"; readonly message: string };

interface PasswordInputProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly autoComplete: string;
  readonly invalid: boolean;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly describedBy?: string | undefined;
  readonly onChange: (value: string) => void;
}

function PasswordInput({ id, label, value, autoComplete, invalid, inputRef, describedBy, onChange }: PasswordInputProps): ReactElement {
  return (
    <div className={shared.field}>
      <label className={shared.label} htmlFor={id}>
        {label}
      </label>
      <Input
        id={id}
        type="password"
        value={value}
        autoComplete={autoComplete}
        invalid={invalid}
        inputRef={inputRef}
        describedBy={describedBy}
        onChange={onChange}
      />
    </div>
  );
}

export function PasswordForm({ saving, wrongCurrent, doneAt, waitSeconds, onSubmit }: PasswordFormProps): ReactElement {
  const { t } = useTranslation();
  const ids = { title: useId(), current: useId(), next: useId(), confirm: useId(), help: useId(), error: useId(), nextError: useId(), formError: useId() };
  const currentRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<"missing" | "mismatch" | null>(null);
  const [nextError, setNextError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const waiting = waitSeconds !== null;

  const submit = async (): Promise<void> => {
    setNextError(null);
    setFormError(null);
    if (current === "" || next === "") {
      setError("missing");
      (current === "" ? currentRef : nextRef).current?.focus();
      return;
    }
    if (isBelowMinimum(next)) {
      setError(null);
      setNextError(t("account.password.tooShort"));
      nextRef.current?.focus();
      return;
    }
    if (next !== confirm) {
      setError("mismatch");
      confirmRef.current?.focus();
      return;
    }
    setError(null);
    const outcome = await onSubmit(current, next);
    if (outcome.kind === "changed") {
      setCurrent("");
      setNext("");
      setConfirm("");
    } else if (outcome.kind === "newRefused") {
      // The refusal is about the value that was sent; once edited, it no longer describes the field.
      if (nextRef.current?.value !== next) return;
      setNextError(outcome.message);
      nextRef.current.focus();
    } else if (outcome.kind === "formRefused") {
      setFormError(outcome.message);
      currentRef.current?.focus();
    } else {
      currentRef.current?.focus();
    }
  };

  return (
    <form
      className={shared.card}
      aria-labelledby={ids.title}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <div className={shared.head}>
        <h2 className={shared.title} id={ids.title}>
          {t("account.password.title")}
        </h2>
      </div>
      <div className={shared.body}>
        {wrongCurrent ? (
          <div className={shared.noticeDanger} role="alert">
            <Icon name="alert-circle" size="sm" />
            <span>{t("account.password.wrongCurrent")}</span>
          </div>
        ) : null}
        {doneAt === null ? null : (
          <div className={shared.noticeInfo} role="status">
            <Icon name="check-circle" size="sm" />
            <span>{t("account.password.done", { time: doneAt })}</span>
          </div>
        )}
        {waiting ? <RateLimitWait seconds={waitSeconds} /> : null}
        <PasswordInput
          id={ids.current}
          label={t("account.password.current")}
          value={current}
          autoComplete="current-password"
          invalid={wrongCurrent}
          inputRef={currentRef}
          onChange={(value) => {
            setCurrent(value);
            setError(null);
          }}
        />
        <div className={shared.field}>
          <PasswordInput
            id={ids.next}
            label={t("account.password.new")}
            value={next}
            autoComplete="new-password"
            invalid={nextError !== null}
            inputRef={nextRef}
            describedBy={nextError === null ? ids.help : `${ids.help} ${ids.nextError}`}
            onChange={(value) => {
              setNext(value);
              setError(null);
              setNextError(null);
            }}
          />
          <PasswordStrength id={ids.help} password={next} />
          {nextError === null ? null : (
            <p className={shared.error} id={ids.nextError} role="alert">
              {nextError}
            </p>
          )}
        </div>
        <PasswordInput
          id={ids.confirm}
          label={t("account.password.confirm")}
          value={confirm}
          autoComplete="new-password"
          invalid={error === "mismatch"}
          inputRef={confirmRef}
          describedBy={error === null ? undefined : ids.error}
          onChange={(value) => {
            setConfirm(value);
            setError(null);
          }}
        />
        {error === null ? null : (
          <p className={shared.error} id={ids.error} role="alert">
            {t(error === "missing" ? "account.password.missing" : "account.password.mismatch")}
          </p>
        )}
        {formError === null ? null : (
          <p className={shared.error} id={ids.formError} role="alert">
            {formError}
          </p>
        )}
      </div>
      <div className={shared.foot}>
        <Button variant="primary" type="submit" disabled={saving || waiting} loading={saving}>
          {t("account.password.submit")}
        </Button>
      </div>
    </form>
  );
}
